import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation, useAuthSession } from '@dukanti/shared';
import { useStockWorklist, stockWorklistKeys, stockWorklistErrorKey } from '@dukanti/shared/admin';
import { useReceivingAccess, useReceivingList } from '@dukanti/shared/hooks';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { Button } from '@/components/ui/button';
import {
  StockSummaryHeader,
  StockFilterBar,
  StockTable,
  StockMobileCards,
  BarcodeSearchCompact,
} from '@/features/stock/components';
import {
  buildStockWorklistPath,
  parseStockWorklistParams,
  readStockReturnAnchor,
  saveStockReviewAnchor,
  saveStockReturnAnchor,
  setStockAnchorIdentity,
  type StockWorklistContext,
} from '@/features/stock/lib/stock-status';

export function StockRoute(): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const context = parseStockWorklistParams(params);
  const returnTo = buildStockWorklistPath(context);
  const authSession = useAuthSession(supabase);
  const {
    storeId,
    isLoading: isStoreLoading,
    isError: isStoreError,
  } = useCurrentStore(authSession);
  const userId = authSession.user?.id ?? null;
  const queryClient = useQueryClient();
  const stock = useStockWorklist(supabase, {
    storeId: context.invalidQuery ? null : storeId,
    userId,
    scope: context.scope,
    filter: context.filter,
    query: context.query,
  });
  const transition = useRef(0);
  const currentIdentity = useRef('');
  currentIdentity.current = JSON.stringify([storeId, userId]);
  const restorePending = useRef(true);
  const worklistElement = useRef<HTMLElement | null>(null);
  const refreshFrom = useRef<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const canRetainPosition =
    !authSession.isLoading &&
    !isStoreLoading &&
    !isStoreError &&
    !stock.accessDenied &&
    Boolean(storeId && userId && stock.page);

  useLayoutEffect(() => {
    if (stock.accessDenied || (!authSession.isLoading && !userId)) {
      setStockAnchorIdentity(null, null);
    } else if (!authSession.isLoading && !isStoreLoading && !isStoreError) {
      // A newly mounted route briefly hydrates its own session after the shell
      // has authorized it. That pending state is not an identity change.
      setStockAnchorIdentity(storeId, userId);
    }
  }, [authSession.isLoading, isStoreLoading, isStoreError, stock.accessDenied, storeId, userId]);
  useLayoutEffect(() => {
    if (!restorePending.current || !canRetainPosition) return;
    const anchor = readStockReturnAnchor(returnTo);
    restorePending.current = false;
    if (anchor && stock.rows.some((row) => row.variantId === anchor.variantId))
      window.scrollTo({ top: anchor.scrollY });
  }, [returnTo, canRetainPosition, stock.rows]);
  useLayoutEffect(() => {
    if (!canRetainPosition) return;
    const element = worklistElement.current;
    if (!element) return;
    let frame: number | null = null;
    const capture = (): void => {
      if (!element.isConnected) return;
      saveStockReviewAnchor(
        returnTo,
        element.querySelectorAll('[data-variant-id]'),
        window.scrollY,
      );
    };
    const scheduleCapture = (): void => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        capture();
      });
    };
    capture();
    window.addEventListener('scroll', scheduleCapture, { passive: true });
    window.addEventListener('blur', capture);
    return () => {
      window.removeEventListener('scroll', scheduleCapture);
      window.removeEventListener('blur', capture);
      if (frame !== null) window.cancelAnimationFrame(frame);
      // Layout cleanup runs before the shell's loading view removes the long
      // list and clamps window.scrollY. Keep positions, never protected rows.
      capture();
    };
  }, [returnTo, canRetainPosition, stock.rows]);
  useEffect(() => {
    if (
      refreshFrom.current &&
      stock.page &&
      stock.page.observedAt !== refreshFrom.current &&
      !stock.isRefreshing
    ) {
      setAnnouncement(t('admin.stock.worklist.restarted'));
      refreshFrom.current = null;
    }
  }, [stock.page, stock.isRefreshing, t]);

  const changeContext = useCallback(
    (next: Pick<StockWorklistContext, 'scope' | 'filter' | 'query'>): void => {
      const path = buildStockWorklistPath(next);
      if (path === returnTo) return;
      const revision = ++transition.current;
      const identity = JSON.stringify([storeId, userId]);
      restorePending.current = false;
      const queryKey = stockWorklistKeys.list({ storeId, userId, ...next });
      void (async (): Promise<void> => {
        await queryClient.cancelQueries({ queryKey, exact: true });
        if (revision !== transition.current || currentIdentity.current !== identity) return;
        queryClient.removeQueries({ queryKey, exact: true });
        navigate(path, { replace: true });
      })();
    },
    [returnTo, storeId, userId, queryClient, navigate],
  );
  const search = useCallback(
    (query: string): void => changeContext({ ...context, query }),
    [changeContext, context],
  );
  const clear = (): void => changeContext({ ...context, filter: 'all', query: '' });
  const viewProduct = (variantId: string): void => {
    setStockAnchorIdentity(storeId, userId);
    saveStockReturnAnchor(returnTo, variantId, window.scrollY);
  };
  const refresh = (): void => {
    refreshFrom.current = stock.page?.observedAt ?? null;
    setAnnouncement('');
    stock.refresh();
  };

  // F-148 §10.1 gated entry: shown when receiving is enabled, or (paused) only if receipts exist.
  const receivingAccess = useReceivingAccess(supabase, storeId);
  const receivingCanManage = receivingAccess.data?.canManage ?? false;
  const receivingEnabled = receivingAccess.data?.enabled ?? false;
  const pausedReceipts = useReceivingList(
    supabase,
    receivingCanManage && !receivingEnabled ? storeId : null,
    'receipt',
  );
  const showReceivingEntry =
    receivingCanManage &&
    (receivingEnabled || (pausedReceipts.data?.pages[0]?.items.length ?? 0) > 0);

  const page = stock.page;
  const initialError = stock.accessDenied
    ? 'admin.stock.worklist.accessDenied'
    : context.invalidQuery
      ? 'admin.stock.worklist.searchTooLong'
      : stock.initialError
        ? stockWorklistErrorKey(stock.initialError)
        : null;
  const noVariants = page?.catalogVariantCount === 0;
  const noActive = !noVariants && context.scope === 'active' && page?.scopeCounts.variants === 0;
  const noMatches = !noVariants && !noActive && page?.matchingCount === 0;
  const checkedTime = page
    ? new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(page.observedAt),
      )
    : null;

  return (
    <main
      ref={worklistElement}
      dir={dir}
      data-testid="stock-route"
      className="mx-auto w-full min-w-0 max-w-7xl space-y-4 px-4 py-5 sm:px-6 lg:px-8"
    >
      <div className="flex items-start justify-between gap-3">
        <h1 className="font-display text-foreground text-start text-[28px] font-bold leading-tight">
          {t('admin.stock.title')}
        </h1>
        <Button
          type="button"
          variant="outline"
          disabled={
            stock.isRefreshing || stock.isLoading || context.invalidQuery || !storeId || !userId
          }
          onClick={refresh}
          data-testid="stock-refresh-button"
          className="min-h-11 shrink-0"
        >
          <RefreshCw
            aria-hidden="true"
            size={16}
            className={stock.isRefreshing ? 'animate-spin' : ''}
          />
          <span className="ms-2">{t('admin.stock.refresh')}</span>
        </Button>
      </div>
      {page && stock.refreshError ? (
        <p
          role="alert"
          data-testid="stock-refresh-error"
          className="text-foreground flex items-start gap-2 text-sm"
        >
          <AlertTriangle aria-hidden="true" size={16} className="mt-0.5 shrink-0" />
          <span>{t('admin.stock.worklist.refreshFailed')}</span>
        </p>
      ) : null}
      <StockSummaryHeader
        counts={page?.scopeCounts ?? null}
        scope={context.scope}
        isLoading={stock.isLoading}
      />
      <StockFilterBar
        scope={context.scope}
        onScopeChange={(scope) => changeContext({ ...context, scope })}
        filter={context.filter}
        onFilterChange={(filter) => changeContext({ ...context, filter })}
        searchQuery={context.query}
        onSearchChange={search}
        onClear={clear}
        matchingCount={page?.matchingCount ?? null}
        loadedCount={stock.rows.length}
        resetKey={JSON.stringify([storeId, userId])}
      />
      {initialError ? (
        <div
          role="alert"
          data-testid="stock-error"
          className="border-border bg-card rounded-xl border p-6 text-center"
        >
          <p className="text-foreground mb-4">{t(initialError)}</p>
          <Button
            variant="outline"
            className="min-h-11"
            data-testid="stock-error-retry"
            onClick={
              context.invalidQuery || stock.initialError?.code === 'invalid_request'
                ? clear
                : refresh
            }
          >
            {t(
              context.invalidQuery || stock.initialError?.code === 'invalid_request'
                ? 'admin.stock.worklist.clear'
                : 'common.retry',
            )}
          </Button>
        </div>
      ) : stock.isLoading ? (
        <div
          role="status"
          data-testid="stock-table-skeleton"
          className="border-border bg-card space-y-4 rounded-xl border p-4"
        >
          <span className="sr-only">{t('admin.stock.loading')}</span>
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="bg-admin-surface-muted h-14 animate-pulse rounded-md" />
          ))}
        </div>
      ) : noVariants || noActive || noMatches ? (
        <div
          data-testid={
            noVariants
              ? 'stock-empty-no-products'
              : noActive
                ? 'stock-empty-no-active'
                : 'stock-empty-no-results'
          }
          className="border-border bg-card rounded-xl border p-6 text-center"
        >
          <p className="text-muted-foreground mb-4">
            {t(
              noVariants
                ? 'admin.stock.worklist.noVariants'
                : noActive
                  ? 'admin.stock.worklist.noActive'
                  : 'admin.stock.worklist.noMatches',
            )}
          </p>
          {noVariants ? (
            <Link
              to="/products"
              className="text-foreground focus-visible:ring-ring inline-flex min-h-11 items-center rounded-md px-3 underline focus-visible:ring-2"
            >
              {t('admin.stock.worklist.openProducts')}
            </Link>
          ) : (
            <Button
              variant="outline"
              className="min-h-11"
              onClick={noActive ? () => changeContext({ ...context, scope: 'all' }) : clear}
            >
              {t(noActive ? 'admin.stock.worklist.showAll' : 'admin.stock.worklist.clear')}
            </Button>
          )}
        </div>
      ) : stock.rows.length > 0 ? (
        <>
          <StockTable rows={stock.rows} returnTo={returnTo} onViewProduct={viewProduct} />
          <StockMobileCards rows={stock.rows} returnTo={returnTo} onViewProduct={viewProduct} />
        </>
      ) : null}
      {page ? (
        <div className="space-y-3">
          {stock.loadMoreError ? (
            <div role="alert" data-testid="stock-load-more-error" className="space-y-2">
              <p className="text-foreground flex items-start gap-2 text-sm">
                <AlertTriangle aria-hidden="true" size={16} className="mt-0.5 shrink-0" />
                <span>{t('admin.stock.worklist.pageFailed')}</span>
              </p>
              <Button
                variant="outline"
                className="min-h-11"
                disabled={stock.isLoadingMore || stock.isRefreshing}
                onClick={stock.retryLoadMore}
              >
                {t('common.retry')}
              </Button>
            </div>
          ) : null}
          {stock.needsRefresh ? (
            <p role="status" className="text-muted-foreground text-sm">
              {t('admin.stock.worklist.changed')}
            </p>
          ) : null}
          {stock.hasMore && !stock.loadMoreError ? (
            <Button
              variant="outline"
              className="min-h-11 w-full sm:w-auto"
              disabled={stock.isLoadingMore || stock.isRefreshing}
              onClick={stock.loadMore}
              data-testid="stock-load-more"
            >
              {t(
                stock.isLoadingMore
                  ? 'admin.stock.worklist.loadingMore'
                  : 'admin.stock.worklist.loadMore',
              )}
            </Button>
          ) : !stock.hasMore && !stock.needsRefresh && stock.rows.length > 0 ? (
            <p className="text-muted-foreground text-center text-sm">
              {t('admin.stock.worklist.end')}
            </p>
          ) : null}
          <p className="text-muted-foreground text-xs">
            <time dateTime={page.observedAt}>
              {t('admin.stock.worklist.lastChecked', {
                time: checkedTime ?? '',
                // Intl output is rendered as React text, which supplies escaping.
                interpolation: { escapeValue: false },
              })}
            </time>
          </p>
          {stock.automaticRefreshPaused ? (
            <p className="text-muted-foreground text-xs">{t('admin.stock.worklist.paused')}</p>
          ) : null}
        </div>
      ) : null}
      <p role="status" aria-live="polite" className="sr-only">
        {stock.isRefreshing
          ? t('admin.stock.refreshing')
          : stock.isLoadingMore
            ? t('admin.stock.worklist.loadingMore')
            : announcement}
      </p>
      <div className="border-border space-y-3 border-t pt-4">
        <p className="text-muted-foreground text-xs">{t('admin.stock.worklist.availableHelp')}</p>
        <p className="text-muted-foreground text-xs">{t('admin.stock.worklist.lowRule')}</p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {showReceivingEntry ? (
              <Button
                type="button"
                className="min-h-11"
                onClick={() => navigate('/stock/receiving')}
                data-testid="stock-receiving-entry"
              >
                {t('admin.receiving.entryButton')}
              </Button>
            ) : null}
            <Button
              type="button"
              className="min-h-11"
              variant={showReceivingEntry ? 'outline' : 'default'}
              onClick={() => navigate('/stock/barcode')}
              data-testid="stock-barcode-receiving-entry"
            >
              {t('admin.barcode.entry.scanReceiving')}
            </Button>
          </div>
          <BarcodeSearchCompact
            surface="stock"
            onSearch={(rawBarcode) =>
              navigate(`/stock/barcode?barcode=${encodeURIComponent(rawBarcode)}`, {
                state: { prefillBarcode: rawBarcode },
              })
            }
          />
        </div>
      </div>
    </main>
  );
}
